import type React from 'react';
import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useReducer,
} from 'react';

type Id = string;
type State = Record<Id, boolean>;

type Action =
	| { type: 'REGISTER'; id: Id; parent?: Id }
	| { type: 'UNREGISTER'; id: Id }
	| { type: 'TOGGLE'; id: Id };

interface NodeMeta {
	parent?: Id;
	children: Id[];
}

interface Hierarchy {
	openMap: State;
	nodes: Record<Id, NodeMeta>;
}

const CollapseSectionCtx = createContext<{
	state: Hierarchy;
	dispatch: React.Dispatch<Action>;
	collapseBranch: (id: Id, open?: boolean) => void;
} | null>(null);

function reducer(state: Hierarchy, action: Action): Hierarchy {
	switch (action.type) {
		case 'REGISTER': {
			const { id, parent } = action;

			const newNodes = { ...state.nodes };
			const newOpenMap = { ...state.openMap };

			newNodes[id] = {
				parent: parent || newNodes[id]?.parent,
				children: newNodes[id]?.children || [],
			};
			newOpenMap[id] = true;

			if (parent) {
				if (!newNodes[parent]) {
					newNodes[parent] = { parent: undefined, children: [] };
					newOpenMap[parent] = true;
				}
				const currentChildren = newNodes[parent].children || [];

				if (!currentChildren.includes(id)) {
					newNodes[parent].children = [...currentChildren, id];
				}
			}

			return { ...state, nodes: newNodes, openMap: newOpenMap };
		}

		case 'UNREGISTER': {
			const { id } = action;
			const { [id]: _, ...restOpen } = state.openMap;
			const { [id]: node, ...restNodes } = state.nodes;
			if (node?.parent) {
				const nodeParent = restNodes[node.parent];
				if (nodeParent)
					nodeParent.children = nodeParent.children.filter((c) => c !== id);
			}
			return { ...state, openMap: restOpen, nodes: restNodes };
		}

		case 'TOGGLE': {
			const open = !state.openMap[action.id];
			return {
				...state,
				openMap: { ...state.openMap, [action.id]: open },
			};
		}

		default: {
			return state;
		}
	}
}

function getDescendants(id: Id, nodes: Record<Id, NodeMeta>, acc: Id[] = []) {
	const nodeChildren = nodes[id]?.children;
	if (!nodeChildren) return acc;
	for (const child of nodeChildren) {
		acc.push(child);
		getDescendants(child, nodes, acc);
	}
	return acc;
}

export function CollapseSectionProvider({
	children,
}: {
	children: React.ReactNode;
}) {
	const [state, dispatch] = useReducer(reducer, { openMap: {}, nodes: {} });

	const collapseBranch = useCallback(
		(id: Id) => {
			const willOpen = !state.openMap[id];
			dispatch({ type: 'TOGGLE', id });

			if (!willOpen) {
				for (const dId of getDescendants(id, state.nodes)) {
					if (state.openMap[dId]) dispatch({ type: 'TOGGLE', id: dId });
				}
			}
		},
		[state.nodes, state.openMap]
	);

	return (
		<CollapseSectionCtx.Provider value={{ state, dispatch, collapseBranch }}>
			{children}
		</CollapseSectionCtx.Provider>
	);
}

export const useCollapseSections = () => {
	const ctx = useContext(CollapseSectionCtx);
	if (!ctx) throw new Error('SectionProvider missing');
	return ctx;
};

export const useCollapse = (id: Id, parent?: Id) => {
	const { state, dispatch, collapseBranch } = useCollapseSections();
	const isOpen = state.openMap[id];

	useEffect(() => {
		dispatch({ type: 'REGISTER', id, parent });
		return () => dispatch({ type: 'UNREGISTER', id });
	}, [id, parent, dispatch]);

	const collapse = useCallback(() => collapseBranch(id), [collapseBranch, id]);

	const collapsedStyle = useMemo(() => {
		return isOpen === undefined || isOpen === true
			? undefined
			: { display: 'none' };
	}, [isOpen]);

	const collapsed = useMemo(() => {
		if (isOpen === undefined) return false;
		if (isOpen === true) return false;
		if (isOpen === false) return true;
		return false;
	}, [isOpen]);

	return { collapsedStyle, toggleCollapse: collapse, collapsed };
};
